/* eslint-disable max-lines */
/// Hooks
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {useLazyQuery, useQuery} from "@apollo/client";
import useVisibility from "../../hooks/useVisibility";
import {useTour} from "../../providers/tour";
import useWaitForFileUpload from "../../hooks/useWaitForFileUpload";
import {useCurrentUser, useCurrentAccount} from "../../App.context";

/// Libs
import {getDataWithMock} from "../../tours";
import {analyticsSendEvent} from "../../libs/analytics";
import {getLanguageCode} from "../../i18n";
import {getMainScenario, getScenarioById} from "./challenge-edit.utils";
import {reverse} from "../../routing";

/// Types
// GQL
import {
  avatarsQuery,
  avatarsQueryData,
  avatarsQueryVars,
  competenciesQuery,
  competenciesQueryData,
  query as challengeQuery,
  queryData,
  queryVars,
  useAddActionMutation,
  useAddScenarioMutation,
  useCopyActionMutation,
  useDeleteActionMutation,
  useDeleteScenarioMutation,
  useUpdateActionMutation,
  useUpdateChildrenMutation,
  useUpdateChallengeMutation,
  useUpdateScenarioMutation,
} from "./challenge-edit.graphql";
// Other
import {Challenge, ChallengeInput, ChallengeMode, RpgInstructionParseStatus, ScenarioInput} from "../../schema";
import {Action, ActionInput} from "../../types";

/// Components
// UI and layout
import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import Skeleton from "../../ui/skeleton";
import Button from "../../ui/button";
import {PlayIcon} from "../../ui/icons";
import Breadcrumbs from "../../ui/breadcrumbs";
import Link from "../../ui/link";
import ReactLink from "../../components/ReactLink";
import ScenarioSelector from "../../components/editor/challenge-edit/ScenarioSelector";
import DeleteScenarioModal from "../../components/editor/DeleteScenarioModal";
import {useModal} from "../../components/ModalProvider";
// Pages
import PageNotFoundScene from "../system/page-not-found";
// Other
import ChallengeActionsList from "../../components/editor/ChallengeActionsList";
import EditableItemName from "../../components/editor/EditableItemName";
import ScenarioCustomizeForm from "../../components/editor/ScenarioCustomizeForm";
import ChallengeCustomizeForm from "../../components/editor/ChallengeCustomizeForm";
import ShareButton from "../../components/editor/ShareButton";
import WidgetErrorBoundary, {ErrorWidgetOptions} from "../../components/system/WidgetErrorBoundary";
import FileIsBrokenWidget from "../../components/editor/widgets/FileIsBroken";
import FileUploadingModal from "../../components/editor/FileUploadingModal";
import ChallengeScenariosLength from "../../components/editor/challenge-edit/ChallengeLength";
import ChallengeWarnings from "../../components/editor/challenge-edit/ChallengeWarnings";
// Tour
import EditorChallengeEditTour, {
  useMockData,
  classes as tourClasses,
  isEditorChallengeEditTourMock,
  NAME as tourName,
} from "./challenge-edit.tour";

/// Classes
import cn from "classnames";
import classes from "./challenge-edit.module.css";

interface LocationState {
  from?: string;
}

export default function EditorChallengeEditScene() {
  const {t, i18n} = useTranslation();
  const language = getLanguageCode(i18n.language);
  const location = useLocation<LocationState>();
  const currentPath = location.pathname;

  const {runningTourName, run: runTour, bindToPage: bindTourToPage, mock: tourMock} = useTour();
  const tourMode = runningTourName === tourName;

  useEffect(() => bindTourToPage(tourName), [bindTourToPage]);

  const {id: challengeId} = useParams<{id: string}>();
  const {add: addModal} = useModal();

  useEffect(() => {
    analyticsSendEvent("editorChallengeView", {challengeId});
  }, [challengeId]);

  const [query, {data, loading: fetching, called: challengeQueryCalled, startPolling, stopPolling}] =
    useLazyQuery<queryData, queryVars>(
    challengeQuery,
    {
      variables: {challengeId, language, path: currentPath.includes("editor") ? "editor" : ""},
    },
  );

  const {avatars} =
    useQuery<avatarsQueryData, avatarsQueryVars>(avatarsQuery, {
      variables: {language},
    }).data ?? {};

  const currentAccount = useCurrentAccount();
  const {data: competenciesData, refetch: refetchCompetencies} = useQuery<
    competenciesQueryData, competenciesQueryData
  >(competenciesQuery);
  const {competencies} = competenciesData ?? {};

  useEffect(() => {
    if (currentAccount?.id) {
      refetchCompetencies();
    }
  }, [currentAccount?.id, refetchCompetencies]);

  const isStaff = useCurrentUser()?.isStaff;
  const isExperimental = useCurrentUser()?.experimentalMode;

  useEffect(() => {
    if (challengeId === "tour") {
      runTour(tourName, true);
    } else if (challengeId && currentPath) {
      query({
        variables: {
          challengeId,
          language,
          path: currentPath.includes("editor") ? "editor" : "",
        },
      });
    }
  }, [challengeId, runTour, query, currentPath, language]);

  const mock = useMemo(() => {
    if (isEditorChallengeEditTourMock(tourMock)) {
      return tourMock;
    }
  }, [tourMock]);

  const mockData = useMockData(avatars, mock?.data.challengeName);

  const {
    challenge,
    project,
    scenarios,
    triggerDicts,
    cutWordsDicts,
    hotWordsDicts,
    supportedVoices,
    resultDescriptions,
    parametricResultDescriptions,
  } = useMemo(() => getDataWithMock(data, mockData, tourMode), [data, mockData, tourMode]);

  const loading = !challenge || !avatars;

  const shareChallengeId = challenge?.id ? `${challenge?.id}_${challenge?.accessToken}` : challengeId;

  const [isUploading, waitForFileUpload] = useWaitForFileUpload();

  const allowScenarios = challenge &&
    ![ChallengeMode.COMPETENCE_TEST, ChallengeMode.RPG_MODE].includes(challenge?.mode);

  const [scenariosVisibility, showScenarios, hideScenarios] = useVisibility((scenarios?.length ?? 0) > 1);
  const [statedCurrentScenarioId, setCurrentScenarioId] = useState<string>();

  // Fix for switching to tourMode. TODO: Replace with something better like useEffect on tourMode
  const currentScenarioId = tourMode ? mockData.scenarios![0].id : statedCurrentScenarioId;

  const currentScenario = useMemo(
    () => (scenarios && currentScenarioId ? getScenarioById(scenarios, currentScenarioId) : undefined),
    [scenarios, currentScenarioId],
  );

  // We assuming that first element in scenario array is main
  const mainScenarioId = scenarios ? getMainScenario(scenarios)?.id : undefined;

  useEffect(() => {
    if (!loading && currentScenarioId === undefined && scenarios) {
      setCurrentScenarioId(getMainScenario(scenarios).id);
      if (scenarios.length > 1) {
        showScenarios();
      } else {
        hideScenarios();
      }
    }
  }, [loading, scenarios, currentScenarioId, currentScenario, showScenarios, hideScenarios]);

  const [applyAddActionMutation] = useAddActionMutation();
  const [applyCopyActionMutation] = useCopyActionMutation();
  const [applyUpdateActionMutation] = useUpdateActionMutation();
  const [applyDeleteActionMutation] = useDeleteActionMutation();
  const [applyUpdateChallengeMutation] = useUpdateChallengeMutation();
  const [applyAddScenarioMutation] = useAddScenarioMutation();
  const applyUpdateScenarioMutation = useUpdateScenarioMutation();
  const [applyDeleteScenarioMutation] = useDeleteScenarioMutation();
  const [applyUpdateChildrenMutation] = useUpdateChildrenMutation();

  const shareLink = useMemo(
    () => window.location.origin + reverse("playerPlay", {id: shareChallengeId}),
    [shareChallengeId],
  );

  const onShareClick = useCallback(() => {
    analyticsSendEvent("editorCopyChallengeShareLink", {challengeId});
  }, [challengeId]);

  const onChallengeUpdate = useCallback(
    (data: ChallengeInput) => {
      analyticsSendEvent("editorChallengeUpdate", {challengeId});

      waitForFileUpload(
        applyUpdateChallengeMutation({
          variables: {challengeId, data, language},
        }),
        data,
      );
    },
    [challengeId, language, applyUpdateChallengeMutation, waitForFileUpload],
  );

  const onChallengeRename = useCallback(
    (data: Pick<Challenge, "name">) => {
      analyticsSendEvent("editorChallengeRename", {challengeId});

      applyUpdateChallengeMutation({
        variables: {challengeId, data, language},
      });
    },
    [challengeId, language, applyUpdateChallengeMutation],
  );

  const onChallengePreview = useCallback(() => {
    analyticsSendEvent("editorChallengePreview", {challengeId});
  }, [challengeId]);

  const needsRpgInstructionFile = useMemo(
    () =>
      !!challenge &&
      challenge.mode === ChallengeMode.RPG_MODE &&
      !challenge.instructionFile,
    [challenge],
  );

  const rpgInstructionParsing = useMemo(
    () =>
      !!challenge &&
      challenge.mode === ChallengeMode.RPG_MODE &&
      !!challenge.instructionFile &&
      challenge.rpgInstructionParseStatus === RpgInstructionParseStatus.IN_PROGRESS,
    [challenge],
  );

  useEffect(() => {
    if (rpgInstructionParsing) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [rpgInstructionParsing, startPolling, stopPolling]);

  const onPreviewButtonClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const blockingModal =
        needsRpgInstructionFile
          ? {id: "rpgInstructionRequiredPreview", key: t("editorChallengeEdit.rpgInstructionRequiredModal")}
          : rpgInstructionParsing
            ? {id: "rpgInstructionParsingPreview", key:  t("editorChallengeEdit.rpgInstructionParsingModal")}
            : null;

      if (blockingModal) {
        e.preventDefault();
        const modal = addModal({
          id: blockingModal.id,
          content: blockingModal.key,
          footer: (
            <Button color="primary" onClick={() => modal.remove()}>
              {t("common.ok")}
            </Button>
          ),
        });
        return;
      }
      onChallengePreview();
    },
    [needsRpgInstructionFile, rpgInstructionParsing, addModal, t, onChallengePreview],
  );

  const onActionAdd = useCallback(
    (actionType: Action["__typename"], data: ActionInput) => {
      analyticsSendEvent("editorChallengeAddAction", {
        challengeId,
        scenarioId: currentScenarioId ?? "no",
        actionType,
        position: data?.beforeActionId ? "insert" : "append",
      });

      applyAddActionMutation(actionType, {
        variables: {challengeId, scenarioId: currentScenarioId, data},
      });
    },
    [challengeId, currentScenarioId, applyAddActionMutation],
  );

  const onActionUpdate = useCallback(
    (actionId: Action["id"], actionType: Action["__typename"], data: ActionInput) => {
      analyticsSendEvent("editorChallengeUpdateAction", {challengeId, actionId});

      waitForFileUpload(
        applyUpdateActionMutation(actionType, {
          variables: {challengeId, actionId, data},
        }),
        data,
      );
    },
    [challengeId, applyUpdateActionMutation, waitForFileUpload],
  );

  const onActionDelete = useCallback(
    (actionId: Action["id"]) => {
      analyticsSendEvent("editorChallengeDeleteAction", {challengeId, actionId});

      applyDeleteActionMutation({
        variables: {challengeId, actionId, path: currentPath.includes("editor") ? "editor" : currentPath},
      });
    },
    [challengeId, applyDeleteActionMutation, currentPath],
  );

  const onActionCopy = useCallback(
    (actionId: Action["id"]) => {
      analyticsSendEvent("editorChallengeCopyAction", {challengeId, actionId});

      applyCopyActionMutation({
        variables: {challengeId, actionId, path: currentPath.includes("editor") ? "editor" : currentPath},
      });
    },
    [challengeId, currentPath, applyCopyActionMutation],
  );

  const onScenarioAdd = useCallback(() => {
    applyAddScenarioMutation({
      variables: {
        challengeId,
        data: {name: t("editorChallengeEdit.scenario.secondary")},
        path: currentPath.includes("editor") ? "editor" : currentPath,
      },
    });
  }, [challengeId, t, applyAddScenarioMutation, currentPath]);

  const onScenarioUpdate = useCallback(
    (data: ScenarioInput) => {
      analyticsSendEvent("editorScenarioUpdate", {scenarioId: currentScenarioId!});

      applyUpdateScenarioMutation({
        variables: {
          challengeId,
          scenarioId: currentScenarioId!,
          data,
          path: currentPath.includes("editor") ? "editor" : currentPath,
        },
      });
    },
    [challengeId, currentScenarioId, applyUpdateScenarioMutation, currentPath],
  );

  const onScenarioDelete = useCallback(() => {
    const onDelete = () => {
      setCurrentScenarioId(mainScenarioId);

      analyticsSendEvent("editorScenarioDelete", {scenarioId: currentScenarioId!});
      applyDeleteScenarioMutation({
        variables: {
          challengeId,
          scenarioId: currentScenarioId!,
          path: currentPath.includes("editor") ? "editor" : currentPath,
        },
      });
      modal.remove();
    };

    const onCancel = () => modal.remove();
    if (!scenarios || !currentScenarioId) {
      return undefined;
    }
    const modal = addModal({
      header: <DeleteScenarioModal.Header />,
      content: <DeleteScenarioModal.Content scenarios={scenarios} scenarioId={currentScenarioId} />,
      footer: <DeleteScenarioModal.Footer onConfirm={onDelete} onCancel={onCancel} />,
    });
  }, [mainScenarioId, challengeId, currentScenarioId, applyDeleteScenarioMutation, addModal, scenarios, currentPath]);

  const onFirstScenarioAdd = useCallback(() => {
    onScenarioAdd();
    showScenarios();
  }, [onScenarioAdd, showScenarios]);

  const competenciesFromActionIds = useMemo(() => {
    const result = new Set<string>();

    if (currentScenario) {
      currentScenario.actions.forEach((action: Action) => {
        if (action.__typename === "UserInputAction") {
          if (action.competence) {
            result.add(action.competence.id);
          }
        }

        if (action.__typename === "ChoiceUserInputAction") {
          if (action.competence) {
            result.add(action.competence.id);
          }
        }
      });
    }
    return Array.from(result);
  }, [currentScenario]);

  const onChildrenUpdate = useCallback(() => {
    analyticsSendEvent("editorChallengeChildrenUpdate", {challengeId: challengeId});
    applyUpdateChildrenMutation({
      variables: {challengeId},
    });
  }, [challengeId, applyUpdateChildrenMutation]);

  const onRegenerateChallengeAudioClick = useCallback(() => {
    onChallengeUpdate({
      regenerateAudio: true,
    });
  }, [onChallengeUpdate]);

  const errorWidgets = useMemo(
    () =>
      new Map<string, ErrorWidgetOptions>([
        [
          "FILE_IS_BROKEN",
          {
            widget: FileIsBrokenWidget,
            stopErrorProcessing: true,
          },
        ],
      ]),
    [],
  );

  const warnings = useMemo(() => {
    if (!scenarios || !currentScenarioId) {
      return undefined;
    }

    const isRpg = challenge?.mode === ChallengeMode.RPG_MODE;

    return (
      <ChallengeWarnings
        className={classes.warnings}
        scenarios={scenarios}
        rpgInstructionMissing={isRpg && needsRpgInstructionFile}
        rpgInstructionParsing={isRpg && rpgInstructionParsing}
      />
    );
  }, [scenarios, currentScenarioId, challenge?.mode, needsRpgInstructionFile, rpgInstructionParsing]);

  if (challengeQueryCalled && !fetching && !challenge && challengeId !== "tour") {
    return <PageNotFoundScene />;
  }
  return (
    <>
      <ReactDocumentTitle title={challenge?.name ?? t("editorChallengeEdit.title")} />

      {tourMode && <EditorChallengeEditTour loading={loading} returnToPreviousPage={challengeId === "tour"} />}
      <Layout data-testid="EditorChallengeEditScene">
        <Header />
        <Main>
          <Container className={classes.main}>
            <WidgetErrorBoundary errorWidgets={errorWidgets}>
              <Breadcrumbs className={classes.breadcrumbs}>
                <ReactLink href={reverse("editor")}>{t("breadcrumbs.editor")}</ReactLink>
                {project?.id ? (
                  <ReactLink href={reverse("editorProjectEdit", {id: project.id})}>
                    {project?.name === undefined
                      ? t("breadcrumbs.challenge")
                      : project.name || <span className={classes.notSpecified}>{t("common.notSpecified")}</span>}
                  </ReactLink>
                ) : null}
                <span title={challenge?.name} translate="no">
                  {challenge?.name === undefined
                    ? t("breadcrumbs.challenge")
                    : challenge.name || <span className={classes.notSpecified}>{t("common.notSpecified")}</span>}
                </span>
              </Breadcrumbs>

              <div className={classes.header}>
                {!loading && challenge ? (
                  <EditableItemName
                    key={challenge.id}
                    className={tourClasses.challengeName}
                    item={challenge!}
                    onSave={mock?.nameEditSave ?? onChallengeRename}
                  />
                ) : (
                  <Skeleton.Text height={28} width={250} />
                )}
                <div className={cn(classes.topRightMenu, tourClasses.topRightMenu)}>
                  <Button
                    as={ReactLink}
                    className={tourClasses.participants}
                    href={reverse("accountMembers") + "?challengeId=" + encodeURIComponent(challengeId)}
                  >
                    {t("editorChallengeEdit.participants")}
                  </Button>
                  {isStaff && (
                    <Button color="secondary" onClick={onRegenerateChallengeAudioClick}>
                      {t("editorChallengeEdit.regenerateAudio")}
                    </Button>
                  )}
                  {isExperimental && (
                    <Button as="a" color="secondary" href={challenge?.contentExportUrl ?? undefined}>
                      {t("editorChallengeEdit.exportChallengeContent")}
                    </Button>
                  )}

                  <Button as="a" className={tourClasses.scorm} href={challenge?.scormDownloadUrl}>
                    SCORM 2004
                  </Button>

                  <ShareButton
                    className={tourClasses.shareButton}
                    value={shareLink}
                    name={challenge?.name}
                    onClick={onShareClick}
                    title={t("components.ShareButton.challengeTitle")}
                    helpText={t("editorChallengeEdit.shareButtonHelpText")}
                  />
                  <Button
                    as={ReactLink}
                    className={cn(classes.previewButton, tourClasses.preview)}
                    href={reverse("playerPlay", {id: challengeId}) + "?preview=1"}
                    onClick={onPreviewButtonClick}
                    color="primary"
                  >
                    <PlayIcon className={classes.previewIcon} />
                    {t("editorChallengeEdit.preview")}
                    {warnings}
                  </Button>
                  {isStaff && challenge?.isInitial && (
                    <Button className={classes.previewButton} onClick={onChildrenUpdate} color="success">
                      {t("editorChallengeEdit.updateChildrenChallenges")}
                    </Button>
                  )}
                </div>
              </div>

              <div className={classes.content}>
                <div className={classes.contentMain}>
                  <div className={classes.contentMainEdit}>
                    {scenarios && allowScenarios &&
                      (scenariosVisibility ? (
                        <ScenarioSelector
                          className={cn(classes.scenariosWrapper, tourClasses.scenarios)}
                          scenarios={scenarios}
                          currentScenarioId={currentScenarioId!}
                          onScenarioAdd={onScenarioAdd}
                          onScenarioIdSelect={setCurrentScenarioId}
                        />
                      ) : (
                        <Link className={classes.addScenariosLink} onClick={onFirstScenarioAdd}>
                          {t("editorChallengeEdit.addScenario")}
                        </Link>
                      ))}
                    {!loading && currentScenario ? (
                      challenge?.mode === ChallengeMode.RPG_MODE ? (
                        <p className={classes.rpgNoActionsHint}>
                          {t("editorChallengeEdit.rpgNoActionsHint")}
                        </p>
                      ) : (
                        <ChallengeActionsList
                          className={tourClasses.timeline}
                          challenge={challenge!}
                          scenarios={scenarios}
                          actions={currentScenario.actions}
                          competencies={competencies}
                          onAdd={onActionAdd}
                          onUpdate={onActionUpdate}
                          onDelete={onActionDelete}
                          onCopy={onActionCopy}
                        />
                      )
                    ) : (
                      <ChallengeActionsList.Skeleton />
                    )}
                    {scenarios && currentScenarioId && (
                      <ChallengeScenariosLength
                        className={classes.challengeLength}
                        scenarios={scenarios}
                        currentScenarioId={currentScenarioId}
                      />
                    )}
                  </div>
                  <div className={classes.contentMainInfo}>{warnings}</div>
                </div>
                <div className={classes.contentAside}>
                  {currentScenario &&
                    !loading &&
                    (currentScenario.isMain ? (
                      <ChallengeCustomizeForm
                        className={tourClasses.challengeCustomizeForm}
                        challenge={challenge!}
                        competencies={competencies}
                        competenciesFromActionIds={competenciesFromActionIds}
                        triggerDicts={triggerDicts}
                        cutWordsDicts={cutWordsDicts}
                        hotWordsDicts={hotWordsDicts}
                        avatars={avatars!}
                        supportedVoices={supportedVoices}
                        onSave={onChallengeUpdate}
                        resultDescriptions={resultDescriptions}
                        parametricResultDescriptions={parametricResultDescriptions}
                      />
                    ) : (
                      <ScenarioCustomizeForm
                        key={currentScenarioId}
                        scenario={currentScenario}
                        scenarios={scenarios}
                        onSave={onScenarioUpdate}
                        onDelete={onScenarioDelete}
                      />
                    ))}
                </div>
              </div>
            </WidgetErrorBoundary>
          </Container>
        </Main>
      </Layout>
      {isUploading && <FileUploadingModal />}
    </>
  );
}
